import { useState } from 'react';
import { Textarea } from '../ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Copy, List, Table2, HelpCircle } from 'lucide-react';
import { getSpecificationText, getSpecificationItems, getTransitionFunctionText, buildTransitionTable, buildPdaTransitionList, buildTmTransitionList, applySuperscripts } from '../../utils/automataHelpers';
import { TestingStrings } from './TestingStrings';

const TABS = [
  { id: 'language', label: 'Language Description' },
  { id: 'spec', label: 'Specification' },
  { id: 'transition', label: 'Transition Function' },
  { id: 'testing', label: 'Testing Strings' },
];

export function BottomPanels({
  height, currentAutomaton, updateCurrentAutomaton,
  copyLanguageDescription, copySpecification, copyTransitionFunction,
  onOpenSimulator,
  simResult, simStep,
  isReadOnly,
}) {
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('bottomPanelTab') || 'transition');
  const [transitionViewMode, setTransitionViewMode] = useState('table');

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    localStorage.setItem('bottomPanelTab', tabId);
  };

  const specText = getSpecificationText(currentAutomaton);
  const specItems = getSpecificationItems(currentAutomaton);
  const transText = getTransitionFunctionText(currentAutomaton);

  const isPDA = currentAutomaton?.type === 'PDA';
  const isTM = currentAutomaton?.type === 'TM';
  const tabs = TABS;

  const copyFn = activeTab === 'language' ? copyLanguageDescription
    : activeTab === 'spec' ? copySpecification
    : copyTransitionFunction;

  const copyTestId = activeTab === 'language' ? 'copy-language-btn'
    : activeTab === 'spec' ? 'copy-spec-btn'
    : 'copy-transitions-btn';

  return (
    <div className="flex flex-col bg-card overflow-hidden" style={{ height: `${height}px` }} data-testid="bottom-panel">
      {/* Tab bar */}
      <div className="flex items-center border-b border-border/30 bg-card/50 shrink-0 px-1">
        <div className="flex items-center gap-0.5 flex-1 min-w-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors relative whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground/70'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-1 right-1 h-0.5 bg-primary rounded-full" />
              )}
            </button>
          ))}
        </div>

        {/* Right-side controls */}
        <div className="flex items-center gap-1 shrink-0 pr-1">
          {activeTab === 'transition' && (
            <>
              <button
                className={`p-0.5 rounded transition-colors ${transitionViewMode === 'list' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setTransitionViewMode('list')}
                data-testid="transition-view-list-btn"
              >
                <List className="w-3.5 h-3.5" />
              </button>
              <button
                className={`p-0.5 rounded transition-colors ${transitionViewMode === 'table' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setTransitionViewMode('table')}
                data-testid="transition-view-table-btn"
              >
                <Table2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          {activeTab !== 'testing' && activeTab !== 'stack' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="p-1 hover:bg-muted rounded transition-colors" onClick={copyFn} data-testid={copyTestId}>
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Copy to clipboard</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto min-h-0">
        {activeTab === 'language' && (
          <Textarea
            value={currentAutomaton?.language_description || ''}
            onChange={(e) => updateCurrentAutomaton({ language_description: applySuperscripts(e.target.value) })}
            placeholder="Describe the language this automaton accepts... (use ^n for superscript)"
            className="w-full h-full min-h-[60px] border-0 rounded-none resize-none focus-visible:ring-0 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="language-description"
            disabled={isReadOnly}
          />
        )}

        {activeTab === 'spec' && (
          <div className="p-3">
            {currentAutomaton?.type === 'PDA' && (
              <PdaAcceptanceToggle currentAutomaton={currentAutomaton} updateCurrentAutomaton={updateCurrentAutomaton} isReadOnly={isReadOnly} />
            )}
            {specItems.length > 0 ? (
              <div className="space-y-1" data-testid="specification-text">
                {specItems.map(({ label, tooltip, value, editable }) => (
                  <div key={label} className="flex items-baseline gap-1.5 text-sm font-mono">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground cursor-help border-b border-dotted border-muted-foreground/40">{label}</span>
                      </TooltipTrigger>
                      <TooltipContent side="right">{tooltip}</TooltipContent>
                    </Tooltip>
                    <span className="text-muted-foreground">=</span>
                    {editable ? (
                      editable === 'step_limit' ? (
                        <input
                          type="number"
                          value={value}
                          onChange={(e) => updateCurrentAutomaton({ step_limit: parseInt(e.target.value, 10) || 10000 })}
                          className="w-20 h-5 px-1 text-sm font-mono bg-muted/50 border border-border/50 rounded focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="tm-step-limit"
                          disabled={isReadOnly}
                        />
                      ) : editable === 'blank_symbol' ? (
                        <input
                          type="text"
                          value={value}
                          maxLength={1}
                          onChange={(e) => updateCurrentAutomaton({ blank_symbol: e.target.value || '\u25A1' })}
                          className="w-12 h-5 px-1 text-sm font-mono bg-muted/50 border border-border/50 rounded text-center focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="tm-blank-symbol"
                          disabled={isReadOnly}
                        />
                      ) : (
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => updateCurrentAutomaton({ initial_stack_symbol: e.target.value })}
                          className="w-12 h-5 px-1 text-sm font-mono bg-muted/50 border border-border/50 rounded focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="pda-initial-stack-symbol"
                          disabled={isReadOnly}
                        />
                      )
                    ) : (
                      <span>{value}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">No states defined</span>
            )}
          </div>
        )}

        {activeTab === 'transition' && (
          <div className="p-3">
            {transitionViewMode === 'list' ? (
              <pre className="text-sm font-mono whitespace-pre-wrap" data-testid="transition-text">
                {transText || 'No transitions'}
              </pre>
            ) : currentAutomaton?.type === 'PDA' ? (
              <PdaTransitionTable currentAutomaton={currentAutomaton} />
            ) : currentAutomaton?.type === 'TM' ? (
              <TmTransitionTable currentAutomaton={currentAutomaton} />
            ) : (
              <TransitionTable currentAutomaton={currentAutomaton} />
            )}
          </div>
        )}

        {activeTab === 'testing' && (
          <TestingStrings
            currentAutomaton={currentAutomaton}
            updateCurrentAutomaton={updateCurrentAutomaton}
            onOpenSimulator={onOpenSimulator}
            isReadOnly={isReadOnly}
          />
        )}
      </div>
    </div>
  );
}

function TransitionTable({ currentAutomaton }) {
  if (!currentAutomaton?.states?.length || !currentAutomaton?.transitions?.length) {
    return <span className="text-sm text-muted-foreground">No transitions</span>;
  }

  const { lookup, columns } = buildTransitionTable(currentAutomaton.states, currentAutomaton.transitions);
  if (columns.length === 0) {
    return <span className="text-sm text-muted-foreground">No transitions</span>;
  }

  return (
    <table className="text-sm font-mono border-collapse" data-testid="transition-table">
      <thead className="sticky top-0 bg-card z-10">
        <tr>
          <th className="px-3 py-1.5 border-r border-b border-border/30 text-left font-bold text-muted-foreground whitespace-nowrap">δ</th>
          {columns.map(sym => (
            <th key={sym} className="px-3 py-1.5 border-b border-border/30 text-center font-bold text-muted-foreground whitespace-nowrap">{sym}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {currentAutomaton.states.map(state => (
          <tr key={state.id} className="hover:bg-muted/30 transition-colors">
            <td className="px-3 py-1.5 border-r border-border/30 font-semibold whitespace-nowrap">
              <span className="inline-flex items-center gap-1">
                {state.name}
                {state.is_initial && <span className="px-1 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 leading-none" data-testid={`badge-initial-${state.id}`}>initial</span>}
                {state.is_final && <span className="px-1 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 leading-none" data-testid={`badge-final-${state.id}`}>final</span>}
              </span>
            </td>
            {columns.map(sym => {
              const dests = lookup[`${state.id}|${sym}`];
              return (
                <td key={sym} className="px-3 py-1.5 text-center whitespace-nowrap">
                  {dests ? `{${[...dests].join(', ')}}` : '—'}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}


function PdaAcceptanceToggle({ currentAutomaton, updateCurrentAutomaton, isReadOnly }) {
  const isFinalState = (currentAutomaton.acceptance_mode || 'final_state') === 'final_state';
  return (
    <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border/30">
      <span className={`text-xs font-medium transition-colors ${isFinalState ? 'text-foreground' : 'text-muted-foreground/50'}`}>Final State</span>
      <button
        onClick={() => !isReadOnly && updateCurrentAutomaton({ acceptance_mode: isFinalState ? 'empty_stack' : 'final_state' })}
        disabled={isReadOnly}
        className={`relative w-8 h-[18px] rounded-full transition-colors ${isFinalState ? 'bg-emerald-500/80' : 'bg-sky-500/80'} disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid="pda-acceptance-mode"
      >
        <span className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform ${isFinalState ? 'left-[2px]' : 'left-[14px]'}`} />
      </button>
      <span className={`text-xs font-medium transition-colors ${!isFinalState ? 'text-foreground' : 'text-muted-foreground/50'}`}>Empty Stack</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/50 cursor-help" />
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[260px] text-xs leading-relaxed">
          <p className="font-semibold mb-1">Final State</p>
          <p className="mb-2">Accepts if the automaton is in a final (accepting) state after consuming all input, regardless of stack contents.</p>
          <p className="font-semibold mb-1">Empty Stack</p>
          <p>Accepts if the stack is empty after consuming all input, regardless of which state the automaton is in.</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function PdaTransitionTable({ currentAutomaton }) {
  if (!currentAutomaton?.states?.length || !currentAutomaton?.transitions?.length) {
    return <span className="text-sm text-muted-foreground">No transitions</span>;
  }

  const rows = buildPdaTransitionList(currentAutomaton.states, currentAutomaton.transitions);
  if (rows.length === 0) {
    return <span className="text-sm text-muted-foreground">No transitions</span>;
  }

  return (
    <table className="text-sm font-mono border-collapse" data-testid="pda-transition-table">
      <thead className="sticky top-0 bg-card z-10">
        <tr>
          <th className="px-3 py-1.5 border-b border-border/30 text-left font-bold text-muted-foreground">State</th>
          <th className="px-3 py-1.5 border-b border-border/30 text-center font-bold text-muted-foreground">Input</th>
          <th className="px-3 py-1.5 border-b border-border/30 text-center font-bold text-muted-foreground">Pop</th>
          <th className="px-3 py-1.5 border-b border-border/30 text-center font-bold text-muted-foreground">{'\u2192'}</th>
          <th className="px-3 py-1.5 border-b border-border/30 text-left font-bold text-muted-foreground">Next</th>
          <th className="px-3 py-1.5 border-b border-border/30 text-center font-bold text-muted-foreground">Push</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="hover:bg-muted/30 transition-colors">
            <td className="px-3 py-1.5 font-semibold">{row.from}</td>
            <td className="px-3 py-1.5 text-center">{row.input}</td>
            <td className="px-3 py-1.5 text-center">{row.pop}</td>
            <td className="px-3 py-1.5 text-center text-muted-foreground">{'\u2192'}</td>
            <td className="px-3 py-1.5">{row.to}</td>
            <td className="px-3 py-1.5 text-center">{row.push}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


function TmTransitionTable({ currentAutomaton }) {
  if (!currentAutomaton?.states?.length || !currentAutomaton?.transitions?.length) {
    return <span className="text-sm text-muted-foreground">No transitions</span>;
  }
  const rows = buildTmTransitionList(currentAutomaton.states, currentAutomaton.transitions);
  if (rows.length === 0) return <span className="text-sm text-muted-foreground">No transitions</span>;
  return (
    <table className="text-sm font-mono border-collapse" data-testid="tm-transition-table">
      <thead>
        <tr className="text-xs text-muted-foreground border-b border-border">
          <th className="px-3 py-1.5 text-left font-medium">State</th>
          <th className="px-3 py-1.5 text-center font-medium">Read</th>
          <th className="px-3 py-1.5 text-center font-medium">{'\u2192'}</th>
          <th className="px-3 py-1.5 text-center font-medium">Write</th>
          <th className="px-3 py-1.5 text-left font-medium">Next</th>
          <th className="px-3 py-1.5 text-center font-medium">Move</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="hover:bg-muted/30 transition-colors">
            <td className="px-3 py-1.5 font-semibold">{row.from}</td>
            <td className="px-3 py-1.5 text-center">{row.read}</td>
            <td className="px-3 py-1.5 text-center text-muted-foreground">{'\u2192'}</td>
            <td className="px-3 py-1.5 text-center">{row.write}</td>
            <td className="px-3 py-1.5">{row.to}</td>
            <td className="px-3 py-1.5 text-center">{row.direction}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
